/**
 * Single chapter notes view - displays notes for one chapter only
 */

import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { ReadingNote, NoteType } from '../../types/reading-notes';
import NoteCard from './NoteCard';

interface ChapterNotesViewProps {
  chapterNumber: number;
  chapterName: string;
  notes: ReadingNote[] | null; // null if not loaded yet
  bookId: string;
  onDelete: (noteId: string) => Promise<void>;
  onUpdate: (noteId: string, updates: Partial<ReadingNote>) => Promise<void>;
  onReindex?: (noteId: string) => Promise<void>;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  searchQuery?: string;
  selectedTypes?: Set<NoteType>;
  isLoading?: boolean;
  highlightedNoteId?: string | null;
}

export default function ChapterNotesView({
  chapterNumber,
  chapterName,
  notes,
  bookId,
  onDelete,
  onUpdate,
  onReindex,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  searchQuery = '',
  selectedTypes,
  isLoading = false,
  highlightedNoteId
}: ChapterNotesViewProps) {
  // Ref for infinite scroll sentinel
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || !onLoadMore || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoadingMore]);
  // Filter notes based on search query and selected types
  const filteredNotes = useMemo(() => {
    if (!notes) return null;

    return notes.filter(note => {
      // Type filter (if selectedTypes is provided)
      if (selectedTypes && !selectedTypes.has(note.note_type)) {
        return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const searchableContent = [
          note.content_text,
          note.normalized_content,
          note.user_note,
          ...(note.concept_tags || []),
          ...(note.extracted_concept_tags || [])
        ].filter(Boolean).join(' ').toLowerCase();

        if (!searchableContent.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [notes, searchQuery, selectedTypes]);

  // Group notes by page
  const notesByPage = filteredNotes ? filteredNotes.reduce((acc, note) => {
    if (!acc[note.page_number]) {
      acc[note.page_number] = [];
    }
    acc[note.page_number].push(note);
    return acc;
  }, {} as Record<number, ReadingNote[]>) : {};

  const sortedPages = Object.keys(notesByPage).map(Number).sort((a, b) => a - b);

  // Scroll to highlighted note when it becomes available
  useEffect(() => {
    if (highlightedNoteId && filteredNotes) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        const noteElement = document.getElementById(`note-${highlightedNoteId}`);
        if (noteElement) {
          noteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight animation
          noteElement.classList.add('ring-2', 'ring-cyan-500', 'ring-offset-2', 'dark:ring-offset-slate-900');
          setTimeout(() => {
            noteElement.classList.remove('ring-2', 'ring-cyan-500', 'ring-offset-2', 'dark:ring-offset-slate-900');
          }, 3000);
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [highlightedNoteId, filteredNotes]);

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 dark:border-gray-600 border-t-transparent"></div>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Loading notes...</p>
      </div>
    );
  }

  // No notes state
  if (!notes || notes.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 dark:text-gray-400">No notes in this chapter</p>
      </div>
    );
  }

  // No filtered notes state
  if (filteredNotes && filteredNotes.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 dark:text-gray-400">No notes match your filters</p>
      </div>
    );
  }

  return (
    <div id="notes-export-container" className="space-y-8">
      {sortedPages.map((pageNumber) => {
        const pageNotes = notesByPage[pageNumber];
        return (
          <div key={pageNumber} className="space-y-4">
            {/* Page Header */}
            <div className="flex items-center gap-3 pt-2">
              <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                Page {pageNumber}
              </span>
              <div className="h-px flex-1 bg-gray-200 dark:border-gray-700" />
            </div>

            {/* Notes for this page */}
            <div className="space-y-4">
              {pageNotes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  bookId={bookId}
                  onDelete={onDelete}
                  onUpdate={onUpdate}
                  onReindex={onReindex}
                  searchQuery={searchQuery}
                  isHighlighted={highlightedNoteId === note.id}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Infinite scroll sentinel and loading indicator */}
      {hasMore && (
        <div ref={loadMoreRef} className="py-8 flex justify-center">
          {isLoadingMore ? (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 dark:border-gray-600 border-t-cyan-500"></div>
              <span className="text-sm">Loading more notes...</span>
            </div>
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-500">Scroll for more</span>
          )}
        </div>
      )}
    </div>
  );
}
